import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { AIText } from "@/components/AIText";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Heart, Baby, Home, MapPin, Briefcase, Building2, Car, Sunrise, HeartCrack, Sparkles, Loader2, Download, ArrowLeft } from "lucide-react";

const ICONS = { married: Heart, child: Baby, house: Home, moved: MapPin, job: Briefcase, business: Building2, teen_driver: Car, retired: Sunrise, divorced: HeartCrack };

export default function LifeEvents() {
  const { t, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/life-events").then((r) => setEvents(r.data)); }, []);

  const run = async (ev) => {
    setActive(ev); setAnswer(""); setLoading(true);
    try { const r = await api.post("/life-event", { event: ev.key, language: lang }); setAnswer(r.data.answer); }
    catch { toast.error("Could not load guidance"); } finally { setLoading(false); }
  };

  const exportPdf = () => downloadReport({ title: `Life Event — ${active.label}`, language: lang,
    subtitle: "Insurance items worth reviewing", sections: [{ heading: active.label, body: answer }] });

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Sparkles className="h-7 w-7 text-brandblue" /> {t("life_events_title")}</h1>
      <p className="mt-1 text-slate-500">{t("life_events_sub")}</p>

      {!active ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {events.map((ev) => {
            const Icon = ICONS[ev.key] || Sparkles;
            return (
              <button key={ev.key} data-testid={`life-${ev.key}`} onClick={() => run(ev)}
                className="flex flex-col items-center gap-3 rounded-2xl bg-white border border-slate-100 card-shadow p-6 hover:-translate-y-1 transition-transform">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient text-white"><Icon className="h-6 w-6" /></span>
                <span className="text-sm font-medium text-navy text-center leading-tight">{ev.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-6">
          <button onClick={() => { setActive(null); setAnswer(""); }} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("all_life_events")}</button>
          <div className="mt-3 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-navy">{active.label}</h2>
              {answer && <Button data-testid="life-export" variant="outline" onClick={exportPdf} className="rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_pdf")}</Button>}
            </div>
            {loading ? <div className="mt-6 flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> {t("thinking_review")}</div>
              : <div className="mt-3"><AIText>{answer}</AIText></div>}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
